import type { TriggerSpec } from '@reef/schema';

/**
 * A trigger, as a sentence.
 *
 * Shared by the factory's stage detail and the workflow catalogue, which is
 * why it is a module rather than a helper at the bottom of one view: the two
 * screens describe the same committed trigger, and two copies of this switch
 * had already been written before the catalogue needed a third. A reader who
 * sees "Linear issue.created — every issue" on the stage should read the same
 * words on the catalogue row, or one of the two looks wrong.
 */
export function describeTrigger(trigger: TriggerSpec): string {
  switch (trigger.type) {
    case 'manual':
      return trigger.triggerFromEveryone ? 'anyone who can see it may start it' : 'editors may start it by hand';
    case 'schedule':
      return trigger.cron ? `on the schedule ${trigger.cron} (UTC)` : 'on a delay';
    case 'linear': {
      const where = [
        trigger.state ? `state "${trigger.state}"` : null,
        trigger.labels?.length ? `labels ${trigger.labels.join(', ')}` : null,
        trigger.teams?.length ? `teams ${trigger.teams.join(', ')}` : null,
      ].filter(Boolean);
      return `Linear ${trigger.events.join(', ')}${where.length ? ` — ${where.join('; ')}` : ' — every issue'}`;
    }
    case 'github': {
      const where = [
        trigger.branches?.length ? `branches ${trigger.branches.join(', ')}` : null,
        trigger.labels?.length ? `labels ${trigger.labels.join(', ')}` : null,
        trigger.repos?.length ? `repos ${trigger.repos.join(', ')}` : null,
      ].filter(Boolean);
      return `GitHub ${trigger.events.join(', ')}${where.length ? ` — ${where.join('; ')}` : ''}`;
    }
    case 'jira':
      return `Jira ${trigger.events.join(', ')}`;
    case 'slack':
      return `Slack ${trigger.events.join(', ')}`;
    case 'webhook':
      return `webhook events ${trigger.events.join(', ')}`;
    default:
      return 'an event';
  }
}

/**
 * Every trigger on one line, for a table cell.
 *
 * The full sentence per trigger is for the stage detail, where there is room.
 * A row gets the short form — the source and its first event — because a
 * cell that wraps to three lines for one workflow and none for the next makes
 * the column unscannable, which is the one thing a catalogue column is for.
 * The sentences are kept in the title so a hover still has them.
 */
export function summarizeTriggers(triggers: readonly TriggerSpec[]): string {
  if (triggers.length === 0) return 'another stage';
  return triggers.map(shortTrigger).join(' · ');
}

function shortTrigger(trigger: TriggerSpec): string {
  switch (trigger.type) {
    case 'manual':
      return 'manual';
    case 'schedule':
      return trigger.cron ? `cron ${trigger.cron}` : 'delay';
    case 'linear':
    case 'github':
    case 'jira':
    case 'slack':
    case 'webhook': {
      const [first] = trigger.events;
      const more = trigger.events.length > 1 ? ` +${trigger.events.length - 1}` : '';
      return `${trigger.type} ${first ?? ''}${more}`.trim();
    }
    default:
      return 'event';
  }
}
